import { useEffect, useRef, useState, useCallback, memo } from "react";
import styles from "./FilteredModal.module.css"
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { setCurrentModule } from "../../../store/reducers/progressionReducer";
import { useDispatch } from "react-redux";
import { displayContentModal } from "../../../utils";
import ImageSlider from "../ImageSlider/ImageSlider";
import ImageSolo from "../ImageSlider/ImageSolo";

// Composants mémorisés pour éviter les re-rendus inutiles
const MemoizedImageSlider = memo(ImageSlider);
const MemoizedImageSolo = memo(ImageSolo);

export default function FilteredModal({ title, tagList, articles }) {
    const [currentIdx, setCurrentIdx] = useState(0);
    const [tags, setTags] = useState([]);
    const [articleList, setArticleList] = useState(articles);
    const [filter, setFilter] = useState("");
    const [currentPageIdx, setCurrentPageIdx] = useState(0);
    const { contextSafe } = useGSAP();
    const dispatch = useDispatch();
    const [showFilters, setShowFilters] = useState(true);

    // Fonction pour afficher le contenu d'une page - mémorisée
    const displayPageContent = useCallback((priority, pageIndex, currentArticle) => {
        if (!currentArticle) return null;

        const { texts, images, buttons, introContent, title: articleTitle } = currentArticle;
        let textIndex = 0;
        let imageIndex = 0;
        let buttonIndex = 0;
        let introIndex = 0;

        // Si c'est un tableau (page avec plusieurs éléments)
        if (Array.isArray(priority)) {
            return (
                <div key={`page-${pageIndex}`} className={styles.articlePage}>
                    {priority.map((element, elementIndex) => {
                        if (element === "intro") {
                            const intro = introContent?.[introIndex++];
                            return (
                                <div key={`intro-${elementIndex}`} className={styles.articleIntro}>
                                    <h1 className={styles.articleTitle}>{articleTitle}</h1>
                                    {intro && (
                                        <div className={styles.introContent}>
                                            <h2>{intro[0]}</h2>
                                            <p>{intro[1]}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        } else if (element === "text") {
                            return (
                                <p key={`text-${elementIndex}`} className={styles.articleText}>
                                    {texts?.[textIndex++]}
                                </p>
                            );
                        } else if (element === "image") {
                            const currentImage = images?.[imageIndex++];
                            if (!currentImage) return null;

                            return (
                                <div key={`image-${elementIndex}`} className={styles.articleImage}>
                                    {currentImage.type === "slider" ? (
                                        <MemoizedImageSlider
                                            images={currentImage}
                                            key={`slider-${currentImage.src[0]}`}
                                        />
                                    ) : (
                                        <MemoizedImageSolo
                                            images={currentImage}
                                            adaptiveHeight={true}
                                            key={`solo-${currentImage.src}`}
                                        />
                                    )}
                                </div>
                            );
                        } else if (element === "button") {
                            const currentButton = buttons?.[buttonIndex++];
                            if (!currentButton) return null;

                            return (
                                <a
                                    key={`button-${elementIndex}`}
                                    href={currentButton.href}
                                    className={styles.articleLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span>{currentButton.content}</span>
                                    <span className={styles.linkArrow}>→</span>
                                </a>
                            );
                        }
                        return null;
                    })}
                </div>
            );
        }

        // Si c'est un élément simple
        if (priority === "text") {
            return (
                <p key={`text-${pageIndex}`} className={styles.articleText}>
                    {texts?.[textIndex++]}
                </p>
            );
        } else if (priority === "image") {
            const currentImage = images?.[imageIndex++];
            if (!currentImage) return null;

            return (
                <div key={`image-${pageIndex}`} className={styles.articleImage}>
                    {currentImage.type === "slider" ? (
                        <MemoizedImageSlider
                            images={currentImage}
                            key={`slider-${currentImage.src[0]}`}
                        />
                    ) : (
                        <MemoizedImageSolo
                            images={currentImage}
                            adaptiveHeight={true}
                            key={`solo-${currentImage.src}`}
                        />
                    )}
                </div>
            );
        }

        return null;
    }, []);

    // Fonction pour afficher le contenu de l'article actuel - mémorisée pour éviter les re-rendus
    const displayArticleContent = useCallback((currentArticle, currentPageIdx) => {
        if (!currentArticle) return null;

        const { priorities, texts, images, buttons, introContent } = currentArticle;

        if (!priorities || priorities.length === 0) {
            return <p>Contenu non disponible</p>;
        }

        // Si pagination est activée, afficher seulement la page courante
        if (currentArticle.pagination === true) {
            const currentPriority = priorities[currentPageIdx];
            return displayPageContent(currentPriority, currentPageIdx, currentArticle);
        }

        // Sinon afficher tout le contenu
        return priorities.map((priority, idx) => displayPageContent(priority, idx, currentArticle));
    }, [displayPageContent]);

    useEffect(() => {
        const articles = filterName()
        filterTags(articles)
        // Reset à la première page lors du changement d'article
        setCurrentPageIdx(0);
    }, [tags, filter, currentIdx])

    const closeEvent = contextSafe(() => {
        gsap
            .timeline({
                onComplete: () => {
                    dispatch(setCurrentModule("none"));
                },
            })
            .to(".modalFrame", {
                opacity: 0,
                pointerEvents: "none",
                duration: 1,
                ease: "power2.out",
            })
    });

    function onChangeFilter(e) {
        setFilter(e.target.value)
    }

    function onChangeTag(e) {
        if (!tags.includes(e.target.id)) {
            setTags(prev => [...prev, e.target.id])
            return true;
        }
        else {
            const copy = [...tags]
            const index = copy.indexOf(e.target.id);
            if (index !== -1) {
                copy.splice(index, 1);
            }
            setTags(copy)
            return false
        }
    }

    function filterName() {
        if (filter.trim() != "") {
            return articles
                .filter(item => item.title.toLowerCase().includes(filter.trim().toLowerCase()))
                .sort((a, b) => a.title.localeCompare(b.title))
        }
        else {
            return articles
        }
    }

    function filterTags(articles) {
        if (tags.length >= 1) {
            const filteredArticles = articles.filter(item => {
                return tags.every(tag => {
                    return item.tags.includes(tag)
                })
            });
            setArticleList(filteredArticles);
        }
        else {
            setArticleList(articles);
        }
    }

    const onChangeArticle = (i) => {
        setCurrentIdx(i);
        setCurrentPageIdx(0); // Reset à la première page
    }

    // Si pas d'articles, afficher un message d'erreur
    if (!articles || articles.length === 0) {
        return (
            <div className={styles.filteredModal}>
                <div className={styles.modal}>
                    <div className={styles.windowList}>
                        <div className={styles.articleListHeader}>
                            <h3>Aucun article disponible</h3>
                        </div>
                    </div>
                    <button className={styles.close} onClick={closeEvent}></button>
                </div>
            </div>
        );
    }

    // Récupère l'article actuel
    const currentArticle = articleList[currentIdx] || articles[0];

    // Navigation des pages pour les articles paginés
    function displayPageNavigation() {
        if (!currentArticle?.pagination || !currentArticle?.priorities) return null;

        const totalPages = currentArticle.priorities.length;
        if (totalPages <= 1) return null;

        return (
            <div className={styles.pageNavigation}>
                <button
                    onClick={() => setCurrentPageIdx(Math.max(0, currentPageIdx - 1))}
                    disabled={currentPageIdx === 0}
                    className={styles.navButton}
                    aria-label="Previous page"
                >
                    ←
                </button>

                <span className={styles.pageInfo}>
                    {currentPageIdx + 1} / {totalPages}
                </span>

                <button
                    onClick={() => setCurrentPageIdx(Math.min(totalPages - 1, currentPageIdx + 1))}
                    disabled={currentPageIdx === totalPages - 1}
                    className={styles.navButton}
                    aria-label="Next page"
                >
                    →
                </button>
            </div>
        );
    }

    // Fonction pour basculer la visibilité des filtres
    const toggleFilters = () => {
        setShowFilters(prev => !prev);
    };

    // Fonction pour naviguer vers l'article suivant
    const nextArticle = () => {
        setCurrentIdx((prevIdx) => Math.min(prevIdx + 1, articleList.length - 1));
    };

    // Fonction pour naviguer vers l'article précédent
    const previousArticle = () => {
        setCurrentIdx((prevIdx) => Math.max(prevIdx - 1, 0));
    };

    return (
        <div className={styles.filteredModal}>
            <div className={styles.modal}>
                {/* Article principal - Layout 2 colonnes */}
                <div className={styles.articleContainer}>
                    <div className={styles.articleContent}>
                        {displayArticleContent(currentArticle, currentPageIdx)}
                    </div>
                    {displayPageNavigation()}
                    <div className={styles.articleNavigation}>
                        <button onClick={previousArticle} disabled={currentIdx === 0} className={styles.navButton}>
                            <span>←</span>
                            <span className={styles.navButtonText}>Previous</span>
                        </button>
                        <span className={styles.articlesCount}>
                            {currentIdx + 1} / {articleList.length}
                        </span>
                        <button onClick={nextArticle} disabled={currentIdx === articleList.length - 1} className={styles.navButton}>
                            <span className={styles.navButtonText}>Next</span>
                            <span>→</span>
                        </button>
                    </div>
                </div>

                {/* Boutons de navigation des articles */}


                {/* Liste des articles avec filtres */}
                <div className={styles.windowList}>
                    {/* En-tête de la liste */}
                    <div className={styles.articleListHeader}>
                        <h3>{title} ({articleList.length})</h3>
                    </div>

                    {/* Bouton pour dérouler/masquer les filtres */}
                    <button onClick={toggleFilters} className={styles.toggleFiltersButton}>
                        <h4>Filters</h4>
                        <span>{showFilters ? '🔼' : '🔽'}</span>
                    </button>

                    {/* Section des filtres avec icône d'œil en Unicode */}
                    {showFilters && (
                        <div className={styles.filterSection}>
                            <div className={styles.filterHeader}>

                            </div>

                            {/* Filtre par nom */}
                            <div className={styles.filterBloc}>
                                <label htmlFor="search-input">Search :</label>
                                <input
                                    id="search-input"
                                    type="text"
                                    value={filter}
                                    onChange={onChangeFilter}
                                    placeholder="Filter articles..."
                                    className={styles.searchInput}
                                />
                            </div>

                            {/* Filtres par tags */}
                            {tagList && tagList.length > 0 && (
                                <div className={styles.tagList}>
                                    <label>Tags :</label>
                                    <div className={styles.tagButtons}>
                                        {tagList.map((tag, i) => (
                                            <button
                                                key={i}
                                                id={tag}
                                                onClick={onChangeTag}
                                                className={`${styles.tagButton} ${tags.includes(tag) ? styles.tagActive : ''}`}
                                            >
                                                {tag}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Contenu scrollable - Liste des articles */}
                    <div className={styles.articlesList}>
                        <ul>
                            {articleList.map((elt, i) => (
                                <li key={i}>
                                    <button
                                        onClick={() => onChangeArticle(i)}
                                        className={`${styles.btnChange} ${currentIdx === i ? styles.selected : ''}`}
                                    >
                                        {elt.title}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Bouton de fermeture */}
                <button className={styles.close} onClick={closeEvent}></button>
            </div>
        </div>
    )
}